import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import {
    COURSE_FILTER_ALL,
    COURSE_FILTER_EXPIRED,
    COURSE_FILTER_ON_GOING,
    COURSE_SHOW_NUM,
} from "../../../constant/options";
import CourseCard from "./CourseCard";
import {useTheme} from "@emotion/react";
import CircleLoading from "../../_common/component/CircleLoading";
import {lang} from "../../../lang";
import {useMobileDetect} from "../../../utils/Hooks";
import {sleep} from "../../../common";
import ThreeDotsLoading from "../../_common/component/ThreeDotsLoading";
import {
    BROADCAST_ON_FILTER_BUTTON_CHANGE,
    getImageUrl,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";
import FilterButtons from "./FilterButtons";

/*
    props
    course_list : [{}, {}, ...] = 본인의 모든 course 배열
    is_loading : bool
 */
const CourseList = (props) => {
    const {broadcast} = useApplicationContext();
    const {is_mobile} = useMobileDetect();

    // state
    const [current_course_list, setCurrentCourseList] = useState([]); // 전체 1, 수강중 2, 수강만료 3
    const [show_num, setShowNum] = useState(COURSE_SHOW_NUM);

    // DOM ref
    const scroll_loading_ref = useRef();

    // ref
    const filter_flag = useRef(COURSE_FILTER_ALL);
    const empty_msg = useRef();
    const is_scroll_loading = useRef(false);

    //css style
    const {view} = useComponentStyle(current_course_list, is_mobile);

    useEffect(() => {
        const unlisten = broadcast.listen([BROADCAST_ON_FILTER_BUTTON_CHANGE], ({value}) => {
            filter_flag.current = value;
            updateCurrentCourseList();
            setShowNum(COURSE_SHOW_NUM);
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    });

    const showMoreCourseListByShowNum = async (course_set) => {
        is_scroll_loading.current = true;
        setClassNameShowScrollLoading();

        await sleep(700);
        setCurrentCourseList(course_set.filter((course, i) => i < show_num + COURSE_SHOW_NUM));
        setClassNameShowScrollLoading();

        setShowNum(show_num + COURSE_SHOW_NUM);
        is_scroll_loading.current = false;
    };

    // 무한 스크롤
    const infiniteScroll = useCallback(async () => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 30) {
            if (filter_flag.current === COURSE_FILTER_ALL) {
                // 스크롤 로딩 중 스크롤을 아래로 내리면 재차 호출되는 경우 방지 (무조건 한번 호출되면 loading이 끝나기 전까지 재 호출 X) : is_scroll_loading
                if (
                    current_course_list.length >= 1 &&
                    current_course_list.length < props.course_list.length &&
                    !is_scroll_loading.current
                ) {
                    await showMoreCourseListByShowNum(props.course_list);
                }
            } else if (filter_flag.current === COURSE_FILTER_ON_GOING) {
                let all_on_going_course_set = props.course_list?.filter((course) => !course.is_expired);
                if (
                    current_course_list.length >= 1 &&
                    current_course_list.length < all_on_going_course_set.length &&
                    !is_scroll_loading.current
                ) {
                    await showMoreCourseListByShowNum(all_on_going_course_set);
                }
            } else if (filter_flag.current === COURSE_FILTER_EXPIRED) {
                let all_expired_course_set = props.course_list?.filter((course) => course.is_expired);
                if (
                    current_course_list.length >= 1 &&
                    current_course_list.length < all_expired_course_set.length &&
                    !is_scroll_loading.current
                ) {
                    await showMoreCourseListByShowNum(all_expired_course_set);
                }
            }
        }
    }, [current_course_list.length, props.course_list]);

    useEffect(() => {
        document.addEventListener("scroll", infiniteScroll);
        return () => {
            document.removeEventListener("scroll", infiniteScroll);
        };
    }, [infiniteScroll]);

    // 필터 버튼에 따라 다른 전체, 수강중, 수강만료의 course_list를 세팅
    const updateCurrentCourseList = useCallback(() => {
        if (filter_flag.current === COURSE_FILTER_ALL) {
            setCurrentCourseList(props.course_list.filter((course, i) => i < show_num));

            if (props.course_list.length < 1) {
                empty_msg.current = lang.course_list.empty_on_going;
            }
        } else if (filter_flag.current === COURSE_FILTER_ON_GOING) {
            let on_going_course_set = props.course_list.filter((course) => !course.is_expired);
            setCurrentCourseList(on_going_course_set.filter((course, i) => i < show_num));

            if (props.course_list.filter((course) => !course.is_expired).length < 1) {
                empty_msg.current = lang.course_list.empty_on_going;
            }
        } else if (filter_flag.current === COURSE_FILTER_EXPIRED) {
            let expire_course_set = props.course_list.filter((course) => course.is_expired);
            setCurrentCourseList(expire_course_set.filter((course, i) => i < show_num));

            if (props.course_list.filter((course) => course.is_expired).length < 1) {
                empty_msg.current = lang.course_list.empty_expired;
            }
        }
    }, [props.course_list, show_num]);

    useEffect(() => {
        if (props.course_list) {
            updateCurrentCourseList();
        }
    }, [props.course_list, show_num, updateCurrentCourseList]);

    const setClassNameShowScrollLoading = () => {
        if (scroll_loading_ref.current.classList.value.split(" ").includes("show")) {
            scroll_loading_ref.current.classList.remove("show");
        } else {
            scroll_loading_ref.current.classList.add("show");
        }
    };

    return (
        <view.CourseSection>
            <FilterButtons course_list={props.course_list} filter_flag={filter_flag.current} />
            <view.CourseCardWrapper {...props}>
                {props.is_loading ? (
                    <CircleLoading />
                ) : current_course_list.length >= 1 ? (
                    current_course_list.map((course, i) => <CourseCard key={i} course={course} />)
                ) : (
                    <view.EmptyCourse>
                        <img src={getImageUrl("image/course/course_nothing.png")} alt="내역 없음 이미지" />
                        <span>{empty_msg.current}</span>
                    </view.EmptyCourse>
                )}
            </view.CourseCardWrapper>
            <view.ScrollLoading ref={scroll_loading_ref}>
                <ThreeDotsLoading size={"lg"} />
            </view.ScrollLoading>
        </view.CourseSection>
    );
};

export default CourseList;

export const useComponentStyle = (current_course_list, is_mobile) => {
    const theme = useTheme();
    return {
        view: {
            CourseSection: styled.div({
                width: is_mobile ? "100%" : "70%",
                minWidth: "260px",
                height: "100%",
                marginLeft: !is_mobile && "1.5em",
                marginRight: !is_mobile && "10px",
                paddingTop: is_mobile && "4em",
                "@media(max-width: 320px)": {
                    paddingTop: is_mobile && "3em",
                },
            }),
            FilterButtonSection: styled.div({
                position: is_mobile && "fixed",
                top: is_mobile && "8.2em",
                zIndex: "6000",
                backgroundColor: theme.colors.w,
                borderBottom: is_mobile && `1px solid ${theme.colors.lg1}`,
                height: "5em",
                width: "100%",
                "@media(max-width: 320px)": {
                    top: "7.2em",
                    height: "4.2em",
                },
                "& > .filter-button-wrapper": {
                    width: is_mobile ? "92%" : "100%",
                    height: "100%",
                    margin: "auto",
                    display: "flex",
                    alignItems: "center",
                    div: {
                        marginRight: ".2em",
                        minWidth: "3.5em",
                    },
                },
            }),
            CourseCardWrapper: styled.div((props) => ({
                margin: is_mobile ? "6em auto 1em auto" : "0 auto 1em auto",
                width: is_mobile ? "95%" : "100%",
                height: "auto",
                boxSizing: "border-box",
                display: props.is_loading || is_mobile ? "flex" : "grid",
                flexDirection: (is_mobile || props.is_loading) && "column",
                alignItems: props.is_loading && "center",
                gridTemplateColumns: current_course_list.length >= 1 ? "1fr 1fr" : "1fr",
                gap: "1.8em",
                transition: "gap .3s",
                "@media (max-width: 950px)": {
                    gridTemplateColumns: "1fr",
                },
                "@media (max-width: 320px)": {
                    gap: "1em",
                    margin: "5.2em auto 1em auto",
                },
            })),
            EmptyCourse: styled.div({
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "3em 1.5em",
                fontSize: theme.font_size.f17,
                color: theme.colors.dg2,
                img: {
                    marginBottom: "1em",
                    width: "100px",
                    height: "100%",
                },
            }),
            ScrollLoading: styled.div({
                display: "none",
                padding: is_mobile ? "0 0 1.5em 0" : "1.5em 0 0 0",
                justifyContent: "center",
                "&.show": {
                    display: "flex",
                },
            }),
        },
    };
};
